///
/// EarthBrowser
///
/// Created by Matt Giger on December 14, 2025
/// Copyright (c) 2025 Geodata Labs
///

import { createSlice, type PayloadAction } from '@reduxjs/toolkit'

export type OGCServerType = 'features' | 'sensorthings'

export interface OGCServer {
  id: string
  name: string
  url: string
  serverType: OGCServerType // 'features' or 'sensorthings'
  description?: string
  isDefault?: boolean
  apiKey?: {
    paramName: string
    paramValue: string
  }
  createdAt: number
  updatedAt: number
}

interface OGCServersState {
  servers: OGCServer[]
  selectedServerId: string
}

const DEFAULT_SERVER_ID = 'earthbrowser_default'

const defaultServer: OGCServer = {
  id: DEFAULT_SERVER_ID,
  name: 'EarthBrowser',
  url: '/api/v1/ogc/features',
  serverType: 'features',
  description: 'EarthBrowser hosted OGC API - Features service',
  isDefault: true,
  createdAt: Date.now(),
  updatedAt: Date.now(),
}

const initialState: OGCServersState = {
  servers: [defaultServer],
  selectedServerId: DEFAULT_SERVER_ID,
}

export const ogcServersSlice = createSlice({
  name: 'ogcServers',
  initialState,
  reducers: {
    addServer: (state, action: PayloadAction<Omit<OGCServer, 'id' | 'createdAt' | 'updatedAt'>>) => {
      const newServer: OGCServer = {
        ...action.payload,
        id: `ogc_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
        serverType: action.payload.serverType || 'features', // Default to 'features' for backward compatibility
        createdAt: Date.now(),
        updatedAt: Date.now(),
      }
      state.servers.push(newServer)
    },

    updateServer: (state, action: PayloadAction<{ id: string; updates: Partial<Omit<OGCServer, 'id' | 'createdAt' | 'isDefault'>> }>) => {
      const { id, updates } = action.payload
      const index = state.servers.findIndex(s => s.id === id)

      if (index !== -1) {
        // Prevent updating the default server's critical fields
        if (state.servers[index].isDefault) {
          const safeUpdates = { ...updates }
          delete safeUpdates.url
          state.servers[index] = {
            ...state.servers[index],
            ...safeUpdates,
            updatedAt: Date.now(),
          }
        } else {
          state.servers[index] = {
            ...state.servers[index],
            ...updates,
            updatedAt: Date.now(),
          }
        }
      }
    },

    deleteServer: (state, action: PayloadAction<string>) => {
      const id = action.payload

      // Prevent deleting the default server
      if (id === DEFAULT_SERVER_ID) {
        console.warn('Cannot delete the built-in EarthBrowser server')
        return
      }

      state.servers = state.servers.filter(s => s.id !== id)

      // If deleted server was selected, switch to default
      if (state.selectedServerId === id) {
        state.selectedServerId = DEFAULT_SERVER_ID
      }
    },

    setSelectedServer: (state, action: PayloadAction<string>) => {
      const serverId = action.payload

      // Verify server exists
      if (state.servers.some(s => s.id === serverId)) {
        state.selectedServerId = serverId
      }
    },

    // Initialize default server if it doesn't exist (for migrations)
    ensureDefaultServer: (state) => {
      const hasDefault = state.servers.some(s => s.id === DEFAULT_SERVER_ID)
      if (!hasDefault) {
        state.servers.unshift(defaultServer)
      }

      // Ensure selected server exists
      if (!state.servers.some(s => s.id === state.selectedServerId)) {
        state.selectedServerId = DEFAULT_SERVER_ID
      }
    },
  },
})

export const {
  addServer,
  updateServer,
  deleteServer,
  setSelectedServer,
  ensureDefaultServer,
} = ogcServersSlice.actions

export default ogcServersSlice.reducer

// Selectors
export const selectAllServers = (state: { ogcServers: OGCServersState }) => state.ogcServers.servers
export const selectSelectedServerId = (state: { ogcServers: OGCServersState }) => state.ogcServers.selectedServerId
export const selectSelectedServer = (state: { ogcServers: OGCServersState }) => {
  const server = state.ogcServers.servers.find(s => s.id === state.ogcServers.selectedServerId)
  return server || state.ogcServers.servers[0] // Fallback to first server
}
export const selectServerById = (state: { ogcServers: OGCServersState }, id: string) => {
  return state.ogcServers.servers.find(s => s.id === id) || null
}
export const selectServersByType = (state: { ogcServers: OGCServersState }, serverType: OGCServerType) => {
  return state.ogcServers.servers.filter(s => s.serverType === serverType)
}

